import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, DollarSign, Users, Briefcase, ShieldCheck,
  TrendingUp, Wallet, Activity, Radio, Building2, Home, Calendar,
  FileText, RefreshCw, ExternalLink, Search, MapPin, Clock, LineChart,
  Settings as SettingsIcon, ClipboardList, LayoutDashboard, Sparkles,
  MessageSquareWarning, ImageIcon, ScrollText, Eye,
} from "lucide-react";
import { api, openProtectedFile } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";
import JobPhotoDocumentation from "@/components/JobPhotoDocumentation";

// Sidebar section (from AdminLayout NavLinks) → existing tab value inside this
// page. Sections without a tab counterpart render a placeholder card.
const SECTION_TO_TAB = {
  dispatch:     "dispatch",
  orders:       "clients",
  clients:      "clients",
  cleaners:     "verify",
  verification: "verify",
  escrow:       "ledger",
  payouts:      "ledger",
  disputes:     "disputes",
};

const SECTION_META = {
  dashboard:    { title: "Dashboard",           subtitle: "Live snapshot of the CLEANBAZ marketplace.",                        icon: LayoutDashboard },
  dispatch:     { title: "Live Dispatch",       subtitle: "Every job in every status, in real time.",                          icon: Radio           },
  orders:       { title: "Orders & Bookings",   subtitle: "Every customer booking, from confirmation to invoice.",             icon: ClipboardList   },
  clients:      { title: "Clients",             subtitle: "Everyone who has ever booked a CLEANBAZ cleaner.",                  icon: Users           },
  cleaners:     { title: "Cleaners",            subtitle: "Your marketplace supply — every vetted CLEANBAZ Pro.",              icon: Sparkles        },
  verification: { title: "Vendor Verification", subtitle: "Approve or reject cleaner IDs so they can start earning.",          icon: ShieldCheck     },
  disputes:     { title: "Disputes",            subtitle: "Customer complaints & damage claims — review evidence, record determinations.", icon: MessageSquareWarning },
  escrow:       { title: "Escrow & Payments",   subtitle: "Money in, held, and released — full ledger.",                       icon: DollarSign      },
  payouts:      { title: "Payouts",             subtitle: "80% released to cleaners after service confirmation.",              icon: Wallet          },
  reports:      { title: "Reports",             subtitle: "Deep-dive analytics coming soon.",                                  icon: LineChart       },
  settings:     { title: "Settings",            subtitle: "Platform configuration coming soon.",                               icon: SettingsIcon    },
};

const COMPLAINT_STATUS_STYLES = {
  under_review:        "bg-amber-500/15 text-amber-200 border-amber-400/40",
  no_issue_found:      "bg-slate-500/15 text-slate-200 border-slate-400/30",
  customer_resolution: "bg-teal-500/15 text-teal-200 border-teal-400/40",
  cleaner_resolution:  "bg-blue-500/15 text-blue-200 border-blue-400/40",
  other:               "bg-purple-500/15 text-purple-200 border-purple-400/40",
};
const COMPLAINT_ISSUE_LABEL = {
  damage:                       "Damage",
  missing_or_damaged_property:  "Missing / damaged property",
  cleaning_quality:             "Cleaning quality",
  other:                        "Other",
};

const V_STYLES = {
  approved:          "bg-teal-500/15 text-teal-200 border-teal-400/40",
  pending_id_review: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  unverified:        "bg-slate-500/15 text-slate-300 border-slate-400/30",
  rejected:          "bg-red-500/15 text-red-300 border-red-400/40",
};

const S_STYLES = {
  pending:              "bg-slate-500/20 text-slate-200 border-slate-400/30",
  matched:              "bg-blue-500/20 text-blue-200 border-blue-400/30",
  active:               "bg-teal-500/20 text-teal-200 border-teal-400/40 shadow-[0_0_20px_rgba(20,184,166,0.35)]",
  in_progress:          "bg-teal-500/20 text-teal-200 border-teal-400/40 shadow-[0_0_20px_rgba(20,184,166,0.35)]",
  completed:            "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
  cancelled:            "bg-red-500/20 text-red-200 border-red-400/40",
  cancelled_by_cleaner: "bg-orange-500/20 text-orange-200 border-orange-400/40",
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function KpiCard({ label, value, sub, icon: Icon, tint = "teal", testid }) {
  const tints = {
    teal:   "from-teal-500/25 to-teal-500/5 border-teal-400/30 text-teal-200",
    slate:  "from-slate-500/25 to-slate-500/5 border-slate-400/30 text-slate-200",
    blue:   "from-blue-500/25 to-blue-500/5 border-blue-400/30 text-blue-200",
    amber:  "from-amber-500/25 to-amber-500/5 border-amber-400/30 text-amber-200",
  }[tint];
  return (
    <Card
      data-testid={testid}
      className={`relative overflow-hidden bg-gradient-to-br ${tints} border backdrop-blur-sm`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
      <CardContent className="relative p-5">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-300">{label}</div>
          <Icon className="w-5 h-5 opacity-80" />
        </div>
        <div className="font-display font-black text-3xl lg:text-4xl mt-3 text-white tabular-nums">{value}</div>
        {sub && <div className="text-xs text-slate-300 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const params = useParams();
  const section = SECTION_META[params.section] ? params.section : "dashboard";
  const meta = SECTION_META[section];
  const activeTab = SECTION_TO_TAB[section]; // undefined for dashboard/reports/settings
  const { openUser, openBooking, onDataChanged } = useAdminUi();
  const [stats, setStats] = useState(null);
  const [cleaners, setCleaners] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [live, setLive] = useState(true);
  const [ticker, setTicker] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [reschJob, setReschJob] = useState(null);
  const [reschDate, setReschDate] = useState("");
  // Disputes / Complaints console state
  const [complaints, setComplaints] = useState([]);
  const [complaintQ, setComplaintQ] = useState("");
  const [complaintFilter, setComplaintFilter] = useState("");
  const [openComplaint, setOpenComplaint] = useState(null);
  const [detStatus, setDetStatus] = useState("under_review");
  const [detNote, setDetNote] = useState("");
  // Documents & Verification modal state
  const [docsCleaner, setDocsCleaner] = useState(null);
  const [docsData, setDocsData] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  // Insurance queue state (Vendor Verification tab)
  const [pendingInsurance, setPendingInsurance] = useState([]);
  const [insVerifyRow, setInsVerifyRow] = useState(null);
  const [insVerifyStatus, setInsVerifyStatus] = useState("approved");
  const [insVerifyReason, setInsVerifyReason] = useState("");
  const [insVerifyExpiry, setInsVerifyExpiry] = useState("");
  // Driver's-license queue state (Vendor Verification tab)
  const [pendingDLs, setPendingDLs] = useState([]);

  const openDocs = async (cleaner) => {
    setDocsCleaner(cleaner);
    setDocsData(null);
    setDocsLoading(true);
    try {
      const { data } = await api.get(`/admin/cleaners/${cleaner.user_id}/documents`);
      setDocsData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load documents");
    } finally {
      setDocsLoading(false);
    }
  };

  const refreshDocs = async () => {
    if (!docsCleaner) return;
    try {
      const { data } = await api.get(`/admin/cleaners/${docsCleaner.user_id}/documents`);
      setDocsData(data);
    } catch (e) { /* ignore */ }
  };

  const verifyDoc = async (doc, status, reason = null) => {
    const act = doc.review_action;
    if (!act) return;
    const payload = { [act.id_field]: act.id_value, status };
    if (reason != null) payload.reason = reason;
    try {
      await api.post(act.endpoint, payload);
      toast.success(`Document marked ${status}`);
      refreshDocs();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update document");
    }
  };

  const load = async () => {
    try {
      const [s, c, j, cu] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/cleaners"),
        api.get("/admin/jobs"),
        api.get("/admin/customers"),
      ]);
      setStats(s.data);
      setCleaners(c.data);
      setJobs(j.data);
      setCustomers(cu.data);
    } catch (e) {
      // silently retry on next tick
    }
    // Insurance queue is loaded independently so a transient risk-router
    // failure never blocks the main dashboard from rendering.
    try {
      const { data } = await api.get("/admin/insurance?status=pending_review");
      setPendingInsurance(Array.isArray(data) ? data : []);
    } catch (e) {
      setPendingInsurance([]);
    }
    // Driver's-license queue — separate try/catch for the same reason.
    try {
      const { data } = await api.get("/admin/drivers-license?status=pending");
      setPendingDLs(Array.isArray(data) ? data : []);
    } catch (e) {
      setPendingDLs([]);
    }
  };

  const loadComplaints = async () => {
    try {
      const { data } = await api.get("/admin/complaints" + (complaintFilter ? `?status_filter=${complaintFilter}` : ""));
      setComplaints(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load complaints");
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { return onDataChanged(() => load()); }, [onDataChanged]);
  useEffect(() => {
    if (section === "disputes") loadComplaints();
  }, [section, complaintFilter]);
  // Live auto-refresh every 10s while enabled
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => { setTicker((t) => t + 1); load(); }, 10000);
    return () => clearInterval(id);
  }, [live]);

  const verify = async (cleaner_id, status, reason = null) => {
    await api.post("/admin/cleaners/verify", { cleaner_id, status, reason });
    toast.success(
      status === "approved" ? "Vendor approved and now live" :
      status === "rejected" ? "Vendor rejected" :
      status === "needs_resubmission" ? "Resubmission requested" :
      "Vendor status updated"
    );
    load();
  };
  const rejectWithReason = (cleaner_id) => {
    const reason = window.prompt("Reject reason (visible to the cleaner):", "ID photo is unclear — please re-upload.");
    if (reason == null) return;
    verify(cleaner_id, "rejected", reason.trim() || null);
  };

  // ---------------------------------------------------------------------
  // Driver's-license queue helpers — reuse the existing
  // /admin/cleaners/driver-license/verify endpoint. Approve = "verified",
  // Reject = "rejected" (requires a reason). No other admin action alters
  // the DL state machine.
  // ---------------------------------------------------------------------
  const verifyDL = async (cleaner_id, status, reason = null) => {
    try {
      await api.post("/admin/cleaners/driver-license/verify", {
        cleaner_id, status, reason,
      });
      toast.success(status === "verified" ? "Driver's license approved"
                    : status === "rejected" ? "Driver's license rejected"
                    : `Driver's license ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update DL status");
    }
  };
  const rejectDLWithReason = (cleaner_id) => {
    const reason = window.prompt(
      "Reject reason (visible to the cleaner):",
      "Photo unclear — please re-upload front and back of your license."
    );
    if (reason == null) return;
    verifyDL(cleaner_id, "rejected", reason.trim() || null);
  };

  // ---------------------------------------------------------------------
  // Insurance review helpers — reuse existing /admin/insurance endpoints.
  // Nothing here changes the base verification flow; it just surfaces the
  // pending queue + inline approve/reject inside Vendor Verification.
  // ---------------------------------------------------------------------
  const openInsuranceVerify = (row) => {
    setInsVerifyRow(row);
    setInsVerifyStatus("approved");
    setInsVerifyReason("");
    setInsVerifyExpiry(row?.expiration_date || "");
  };
  const submitInsuranceVerify = async () => {
    if (!insVerifyRow) return;
    if (insVerifyStatus !== "approved" && !insVerifyReason.trim()) {
      toast.error("A rejection reason is required.");
      return;
    }
    try {
      await api.post("/admin/insurance/verify", {
        insurance_id: insVerifyRow.id,
        status: insVerifyStatus,
        reason: insVerifyReason.trim() || null,
        expiration_date_override: insVerifyExpiry || null,
      });
      toast.success(
        insVerifyStatus === "approved" ? "Insurance approved — cleaner eligibility updated." :
        insVerifyStatus === "rejected" ? "Insurance rejected — cleaner blocked from transport jobs." :
        "Insurance marked for resubmission."
      );
      setInsVerifyRow(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save decision");
    }
  };
  const requestResubmission = (cleaner_id) => {
    const reason = window.prompt("What should the cleaner re-submit?", "Please retake the ID photo with all 4 corners visible and no glare.");
    if (reason == null) return;
    verify(cleaner_id, "needs_resubmission", reason.trim() || null);
  };

  const setCarEligibility = async (cleaner_id, status, reason = null) => {
    try {
      await api.post("/admin/cleaners/car-eligibility", { cleaner_id, status, reason });
      toast.success(
        status === "approved" ? "Car Cleaning approved" :
        status === "rejected" ? "Car Cleaning rejected" :
        "Car Cleaning status updated"
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update car eligibility");
    }
  };
  const rejectCarWithReason = (cleaner_id) => {
    const reason = window.prompt("Reject reason (visible to the cleaner):", "Please complete vehicle and insurance requirements before offering Car Cleaning.");
    if (reason == null) return;
    setCarEligibility(cleaner_id, "rejected", reason.trim() || null);
  };

  const openReschedule = (j) => {
    setReschJob(j);
    setReschDate(j.scheduled_at?.slice(0, 16) || "");
  };
  const submitReschedule = async () => {
    if (!reschJob || !reschDate) return;
    try {
      await api.post(`/admin/jobs/${reschJob.id}/reschedule`, {
        scheduled_at: new Date(reschDate).toISOString(),
        waive_late_fee: true,
      });
      toast.success("Booking rescheduled");
      setReschJob(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reschedule");
    }
  };

  const paidJobs = useMemo(() => jobs.filter((j) => j.payment_status === "paid"), [jobs]);
  const activeJobs = useMemo(() => jobs.filter((j) => j.status === "active").length, [jobs]);
  const pendingJobs = useMemo(() => jobs.filter((j) => j.status === "pending" || j.status === "matched").length, [jobs]);

  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (statusFilter !== "all") list = list.filter((j) => j.status === statusFilter);
    if (q.trim()) {
      const qq = q.toLowerCase();
      list = list.filter((j) =>
        j.id.toLowerCase().includes(qq) ||
        j.client?.full_name?.toLowerCase().includes(qq) ||
        j.client?.email?.toLowerCase().includes(qq) ||
        j.cleaner?.full_name?.toLowerCase().includes(qq) ||
        j.country?.toLowerCase().includes(qq) ||
        j.postal_code?.toLowerCase().includes(qq)
      );
    }
    return list;
  }, [jobs, statusFilter, q]);

  const filteredCustomers = useMemo(() => {
    if (!q.trim()) return customers;
    const qq = q.toLowerCase();
    return customers.filter((c) =>
      c.full_name?.toLowerCase().includes(qq) || c.email?.toLowerCase().includes(qq)
    );
  }, [customers, q]);

  const filteredCleaners = useMemo(() => {
    if (!q.trim()) return cleaners;
    const qq = q.toLowerCase();
    return cleaners.filter((c) =>
      c.user?.full_name?.toLowerCase().includes(qq) ||
      c.user?.email?.toLowerCase().includes(qq) ||
      c.city?.toLowerCase().includes(qq) ||
      c.country?.toLowerCase().includes(qq)
    );
  }, [cleaners, q]);

  const downloadInvoice = async (job) => {
    try {
      // Uses ?auth= for direct browser open
      const token = localStorage.getItem("bb_token");
      const url = `${api.defaults.baseURL}/jobs/${job.id}/invoice.pdf?auth=${encodeURIComponent(token)}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error("Unable to open invoice");
    }
  };

  if (!stats) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading command center…</div>;
  }

  const pool = stats.platform_revenue - (stats.platform_withdrawn || 0);
  const paidGmv = stats.gmv;
  const worldCountries = new Set(jobs.map((j) => j.country).filter(Boolean)).size;

  return (
    <div className="relative bg-slate-950 text-slate-100" data-testid={`admin-section-${section}`}>
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-teal-500/10 to-transparent" />
      <div className="pointer-events-none absolute top-40 -left-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        {/* Section header (dynamic per admin sub-route) */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.34em] text-teal-300 flex items-center gap-2">
              <meta.icon className="w-3.5 h-3.5" /> {section}
            </div>
            <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-2 text-white" data-testid="admin-section-title">
              {meta.title}
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">{meta.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-live-toggle"
              onClick={() => setLive((v) => !v)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-mono uppercase tracking-[0.18em] transition-all ${live ? "border-teal-400/70 text-teal-200 bg-teal-500/10 shadow-[0_0_24px_rgba(20,184,166,0.35)]" : "border-slate-600 text-slate-300"}`}
            >
              <span className={`w-2 h-2 rounded-full ${live ? "bg-teal-400 animate-pulse" : "bg-slate-500"}`} />
              {live ? "Live" : "Paused"}
            </button>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-600 text-slate-200 hover:border-teal-400 hover:text-teal-300 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard testid="kpi-revenue"  label="Platform Revenue" value={money(stats.platform_revenue)} sub="20% collected" icon={TrendingUp} tint="teal" />
          <KpiCard testid="kpi-payouts"  label="Cleaner Payouts"   value={money(stats.cleaner_payouts)}   sub="80% distributed" icon={Wallet}     tint="blue" />
          <KpiCard testid="kpi-gmv"      label="Gross Volume"      value={money(paidGmv)}                 sub={`${stats.paid_jobs} paid jobs`} icon={DollarSign} tint="slate" />
          <KpiCard testid="kpi-pool"     label="Profit Pool"       value={money(pool)}                    sub="Real-time balance" icon={Activity} tint="amber" />
        </div>

        {/* Secondary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { l: "Active jobs now", v: activeJobs, i: Radio,        tint: "text-teal-300" },
            { l: "Awaiting dispatch", v: pendingJobs, i: Clock,      tint: "text-amber-300" },
            { l: "Countries live",  v: worldCountries || stats.total_customers > 0 ? worldCountries : 0, i: MapPin, tint: "text-blue-300" },
            { l: "Pending vendor reviews", v: stats.pending_verifications, i: ShieldCheck, tint: "text-rose-300" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm px-4 py-3 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.l}</div>
                <div className="font-display font-bold text-2xl mt-1 tabular-nums">{s.v}</div>
              </div>
              <s.i className={`w-5 h-5 ${s.tint}`} />
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search across jobs, clients, cleaners, countries…"
            data-testid="admin-search"
            className="pl-9 bg-white/[0.04] border-white/10 text-slate-100 placeholder:text-slate-500 focus-visible:ring-teal-400/40"
          />
        </div>

        {section === "dashboard" && (
          <Card className="bg-white/[0.03] border-white/10 text-slate-200" data-testid="admin-dashboard-overview">
            <CardContent className="p-6">
              <h3 className="font-display font-bold text-lg mb-2">Welcome back, {(stats && stats.total_customers != null) ? "Owner" : "Admin"}.</h3>
              <p className="text-sm text-slate-400 max-w-2xl">
                The KPI cards above reflect real-time platform state pulled from Stripe and MongoDB. Use the sidebar to jump into <span className="text-teal-300">Live Dispatch</span> to see every active job, <span className="text-teal-300">Vendor Verification</span> to approve new cleaners, or <span className="text-teal-300">Escrow &amp; Payments</span> to review the ledger.
              </p>
            </CardContent>
          </Card>
        )}

        {(section === "reports" || section === "settings") && (
          <Card className="bg-white/[0.03] border-white/10 text-slate-200" data-testid={`admin-${section}-placeholder`}>
            <CardContent className="p-8 text-center">
              <meta.icon className="w-10 h-10 text-teal-300 mx-auto mb-3" />
              <h3 className="font-display font-bold text-xl">{meta.title} — coming soon</h3>
              <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">{meta.subtitle}</p>
            </CardContent>
          </Card>
        )}

        {activeTab && (
        <Tabs value={activeTab}>
          <TabsList className="hidden">
            <TabsTrigger value="dispatch" data-testid="ad-tab-dispatch"><Radio className="w-4 h-4 mr-1.5" />Live Dispatch</TabsTrigger>
            <TabsTrigger value="verify"   data-testid="ad-tab-verify"><ShieldCheck className="w-4 h-4 mr-1.5" />Vendor Verification</TabsTrigger>
            <TabsTrigger value="clients"  data-testid="ad-tab-clients"><Users className="w-4 h-4 mr-1.5" />Clients &amp; Bookings</TabsTrigger>
            <TabsTrigger value="ledger"   data-testid="ad-tab-ledger"><DollarSign className="w-4 h-4 mr-1.5" />Escrow Ledger</TabsTrigger>
            <TabsTrigger value="disputes" data-testid="ad-tab-disputes"><MessageSquareWarning className="w-4 h-4 mr-1.5" />Disputes</TabsTrigger>
          </TabsList>

          {/* LIVE DISPATCH */}
          <TabsContent value="dispatch" className="mt-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {["all", "pending", "matched", "in_progress", "completed", "cancelled_by_cleaner", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`filter-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.18em] border transition-colors ${statusFilter === s ? "bg-teal-500/20 border-teal-400/60 text-teal-200" : "border-white/10 text-slate-300 hover:border-teal-400/40"}`}
                >
                  {s} · {s === "all" ? jobs.length : jobs.filter((j) => j.status === s).length}
                </button>
              ))}
              <div className="ml-auto text-xs font-mono text-slate-500">tick #{ticker}</div>
            </div>
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="text-left p-3">Job</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Cleaner / Team</th>
                      <th className="text-left p-3">Where</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Total</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map((j) => (
                      <tr key={j.id} data-testid={`disp-row-${j.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="p-3 font-mono text-[11px] text-slate-300">
                          <button onClick={() => openBooking(j.id)} data-testid={`disp-open-${j.id}`} className="text-teal-300 hover:underline">{j.id.slice(0, 8)}</button>
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1.5 text-xs text-slate-200">
                            {j.booking_type === "business" ? <Building2 className="w-3.5 h-3.5 text-teal-300" /> : <Home className="w-3.5 h-3.5 text-teal-300" />}
                            {(j.booking_type || "home").toUpperCase()}
                          </span>
                        </td>
                        <td className="p-3">
                          <button onClick={() => j.client?.id && openUser(j.client.id)} data-testid={`disp-open-client-${j.id}`} className="text-left hover:text-teal-300" disabled={!j.client?.id}>
                            <div className="text-sm text-slate-100">{j.client?.full_name || "—"}</div>
                            <div className="text-[11px] text-slate-500">{j.client?.email}</div>
                          </button>
                        </td>
                        <td className="p-3">
                          {j.cleaner?.full_name ? (
                            <button onClick={() => j.cleaner?.id && openUser(j.cleaner.id)} data-testid={`disp-open-cleaner-${j.id}`} className="text-left hover:text-teal-300" disabled={!j.cleaner?.id}>
                              <div className="text-sm text-slate-100">{j.cleaner.full_name}</div>
                              {j.team_size > 1 && <div className="text-[11px] text-teal-300">+{j.team_size - 1} team</div>}
                            </button>
                          ) : (
                            <div>
                              <span className="text-slate-500 text-xs">— Awaiting —</span>
                              {j.last_cancellation && (
                                <div className="mt-1 text-[10px] leading-tight text-orange-300" title={`${j.last_cancellation.reason}${j.last_cancellation.notes ? ' — ' + j.last_cancellation.notes : ''}`}>
                                  {j.last_cancellation.was_reassigned ? (
                                    <>✓ Reassigned to {j.last_cancellation.reassigned_to_email?.split("@")[0] || "cleaner"} · was: {j.last_cancellation.reason?.replace(/_/g, " ")}</>
                                  ) : (
                                    <>⚠ Cancelled by {j.last_cancellation.cleaner_email?.split("@")[0] || "cleaner"} · {j.last_cancellation.reason?.replace(/_/g, " ")}</>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-xs text-slate-300">
                          {j.street_address || j.address ? (
                            <>
                              <div className="text-slate-100">{j.street_address || j.address}{j.unit ? `, ${j.unit}` : ""}</div>
                              <div className="text-[11px] text-slate-500">{[j.city, j.state, j.postal_code].filter(Boolean).join(", ")}{j.country ? ` · ${j.country}` : ""}</div>
                            </>
                          ) : (
                            <span><span className="font-mono">{j.country || "?"}</span> · {j.postal_code || "—"}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono text-[10px] uppercase tracking-[0.16em] ${S_STYLES[j.status] || S_STYLES.pending}`}>
                            {j.status === "active" && <span className="w-1.5 h-1.5 rounded-full bg-teal-300 animate-pulse" />}
                            {j.status}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-slate-100">{money(j.total)}</td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => openReschedule(j)}
                              data-testid={`resch-${j.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-white/10 text-slate-200 hover:border-teal-400 hover:text-teal-300"
                              title="Reschedule booking"
                            >
                              <Calendar className="w-3.5 h-3.5" /> Reschedule
                            </button>
                            {j.booking_type === "business" && (
                              <button
                                onClick={() => downloadInvoice(j)}
                                data-testid={`invoice-${j.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-teal-400/40 text-teal-200 hover:bg-teal-500/10"
                                title="Open PDF invoice"
                              >
                                <FileText className="w-3.5 h-3.5" /> Invoice
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredJobs.length === 0 && (
                      <tr><td colSpan={8} className="p-10 text-center text-slate-500">No jobs match this filter.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VENDOR VERIFICATION */}
          <TabsContent value="verify" className="mt-6">
            {/* ---------------------------------------------------------
                Insurance Awaiting Review — pulled from /admin/insurance
                (same source as /admin/risk). Admin can view proof,
                approve, or reject-with-reason inline. Backend audit-
                logs every decision and emails the cleaner.
                --------------------------------------------------------- */}
            {pendingInsurance.length > 0 && (
              <Card className="bg-amber-500/[0.06] border-amber-400/30 mb-4" data-testid="verify-insurance-queue">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-amber-300" />
                    <div className="text-xs font-mono uppercase tracking-[0.22em] text-amber-200">
                      Insurance Awaiting Review · {pendingInsurance.length}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {pendingInsurance.map((r) => (
                      <div key={r.id} data-testid={`verify-ins-row-${r.id}`}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-400/20 bg-slate-950/40">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs text-teal-300">{r.cleanbaz_id || "—"}</span>
                            <span className="text-sm font-semibold text-slate-100 truncate">{r.cleaner?.email || "unknown"}</span>
                            <Badge className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-400/30">Pending review</Badge>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            <span className="uppercase text-[10px] tracking-widest text-slate-500">{r.insurance_type}</span>
                            {" · "}<b className="text-slate-200">{r.carrier || "—"}</b>
                            {" · Policy "}<span className="font-mono">••••{r.policy_number_last4 || "----"}</span>
                            {" · Effective "}<span className="font-mono">{r.effective_date || "—"}</span>
                            {" · Exp "}<span className="font-mono">{r.expiration_date || "—"}</span>
                          </div>
                          {r.document_url && (
                            <button type="button"
                               onClick={async () => {
                                 try { await openProtectedFile(r.document_url); }
                                 catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load proof document."); }
                               }}
                               className="text-xs text-teal-300 mt-1 inline-flex items-center gap-1 hover:underline bg-transparent p-0"
                               data-testid={`verify-ins-doc-${r.id}`}>
                              <Eye className="w-3 h-3" /> View proof
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button size="sm" variant="outline" data-testid={`verify-ins-reject-${r.id}`}
                            onClick={() => { openInsuranceVerify(r); setInsVerifyStatus("rejected"); }}
                            className="bg-transparent border-red-400/40 text-red-300 hover:bg-red-500/10">
                            <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                          </Button>
                          <Button size="sm" data-testid={`verify-ins-approve-${r.id}`}
                            onClick={() => { openInsuranceVerify(r); setInsVerifyStatus("approved"); }}
                            className="bg-teal-500 hover:bg-teal-400 text-slate-950">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Review &amp; Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {pendingDLs.length > 0 && (
              <Card className="bg-amber-500/[0.06] border-amber-400/30 mb-4" data-testid="verify-dl-queue">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-amber-300" />
                    <div className="text-xs font-mono uppercase tracking-[0.22em] text-amber-200">
                      Driver's License Awaiting Review · {pendingDLs.length}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {pendingDLs.map((r) => (
                      <div key={r.id} data-testid={`verify-dl-row-${r.user_id}`}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-400/20 bg-slate-950/40">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs text-teal-300">{r.cleanbaz_id || "—"}</span>
                            <span className="text-sm font-semibold text-slate-100 truncate">{r.cleaner?.email || "unknown"}</span>
                            <Badge className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-400/30">Pending review</Badge>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            <span className="uppercase text-[10px] tracking-widest text-slate-500">Driver's License</span>
                            {" · State "}<b className="text-slate-200">{r.dl_state || "—"}</b>
                            {" · Exp "}<span className="font-mono">{r.dl_expiration_date || "—"}</span>
                            {" · Last4 "}<span className="font-mono">••••{r.dl_last4 || "----"}</span>
                            {r.transportation_type ? <> {" · "}<span className="text-slate-400">{r.transportation_type}</span></> : null}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {r.dl_front_url && (
                              <button type="button"
                                onClick={async () => {
                                  try { await openProtectedFile(r.dl_front_url); }
                                  catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load driver's license photo."); }
                                }}
                                className="text-xs text-teal-300 inline-flex items-center gap-1 hover:underline bg-transparent p-0"
                                data-testid={`verify-dl-front-${r.user_id}`}>
                                <Eye className="w-3 h-3" /> View front
                              </button>
                            )}
                            {r.dl_back_url && (
                              <button type="button"
                                onClick={async () => {
                                  try { await openProtectedFile(r.dl_back_url); }
                                  catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load driver's license photo."); }
                                }}
                                className="text-xs text-teal-300 inline-flex items-center gap-1 hover:underline bg-transparent p-0"
                                data-testid={`verify-dl-back-${r.user_id}`}>
                                <Eye className="w-3 h-3" /> View back
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button size="sm" variant="outline" data-testid={`verify-dl-reject-${r.user_id}`}
                            onClick={() => rejectDLWithReason(r.user_id)}
                            className="bg-transparent border-red-400/40 text-red-300 hover:bg-red-500/10">
                            <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                          </Button>
                          <Button size="sm" data-testid={`verify-dl-approve-${r.user_id}`}
                            onClick={() => verifyDL(r.user_id, "verified")}
                            className="bg-teal-500 hover:bg-teal-400 text-slate-950">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-3">
              {filteredCleaners.filter((c) => c.verification_status === "pending_id_review").map((c) => (
                <Card key={c.user_id} data-testid={`verify-card-${c.user_id}`} className="bg-amber-500/[0.06] border-amber-400/30">
                  <CardContent className="p-5 grid md:grid-cols-5 gap-4 items-center">
                    <div>
                      <div className="font-display font-semibold text-slate-100">
                        {c.legal_first_name || c.user?.full_name} {c.legal_last_name}
                      </div>
                      <div className="text-xs text-slate-400">{c.user?.email}</div>
                      {c.cleanbaz_id && (
                        <div className="mt-1 inline-block font-mono text-[10px] tracking-widest px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-200 border border-teal-400/30" data-testid={`verify-cbz-${c.user_id}`}>
                          {c.cleanbaz_id}
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-slate-200">
                      {c.city}{c.city && c.country ? ", " : ""}{c.country}
                      <div className="text-[11px] text-slate-500 mt-0.5">Zones: {(c.postal_codes || []).join(", ") || "—"}</div>
                    </div>
                    <div className="text-sm text-slate-300">
                      Rating <span className="text-teal-300 font-mono">{c.avg_rating || 0}</span>
                      <div className="text-[11px] text-slate-500">{c.jobs_completed || 0} jobs</div>
                    </div>
                    {c.id_document_url ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDocs(c)}
                        data-testid={`view-docs-${c.user_id}`}
                        className="bg-transparent border-teal-400/40 text-teal-200 hover:bg-teal-500/10"
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> View documents
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-500">No documents yet</span>
                    )}
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => requestResubmission(c.user_id)} data-testid={`resubmit-${c.user_id}`} className="bg-transparent border-amber-400/40 text-amber-200 hover:bg-amber-500/10">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />Resubmit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectWithReason(c.user_id)} data-testid={`reject-${c.user_id}`} className="bg-transparent border-red-400/40 text-red-300 hover:bg-red-500/10">
                        <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                      </Button>
                      <Button size="sm" onClick={() => verify(c.user_id, "approved")} data-testid={`approve-${c.user_id}`} className="bg-teal-500 hover:bg-teal-400 text-slate-950">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredCleaners.filter((c) => c.verification_status === "pending_id_review").length === 0 && (
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-10 text-center text-slate-400">All caught up — no vendors awaiting review.</CardContent>
                </Card>
              )}

              {/* All vendors table */}
              <div className="mt-6">
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-slate-400 mb-3">All Vendors ({filteredCleaners.length})</div>
                <Card className="bg-white/[0.03] border-white/10">
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                      <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                        <tr>
                          <th className="text-left p-3">Vendor</th>
                          <th className="text-left p-3">Region</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Agreement</th>
                          <th className="text-right p-3">Rating</th>
                          <th className="text-right p-3">Jobs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCleaners.map((c) => (
                          <tr key={c.user_id} className="border-t border-white/5 cursor-pointer hover:bg-white/[0.03]" onClick={() => openUser(c.user_id)} data-testid={`vendor-row-${c.user_id}`}>
                            <td className="p-3">
                              <div className="text-sm text-slate-100 hover:text-teal-300">{c.user?.full_name}</div>
                              <div className="text-[11px] text-slate-500">{c.user?.email}</div>
                              {(c.service_categories || []).length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {(c.service_categories || []).map((sc) => (
                                    <span key={sc} data-testid={`vendor-cat-${c.user_id}-${sc}`} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10 text-slate-300">{sc}</span>
                                  ))}
                                  {(c.service_categories || []).includes("car") && (
                                    <span
                                      data-testid={`vendor-car-status-${c.user_id}`}
                                      className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                        c.car_cleaning_status === "approved" ? "bg-amber-500/15 text-amber-200 border-amber-400/40" :
                                        c.car_cleaning_status === "rejected" ? "bg-red-500/15 text-red-300 border-red-400/40" :
                                        "bg-amber-500/[0.08] text-amber-300 border-amber-400/20"
                                      }`}
                                    >
                                      car · {c.car_cleaning_status || "pending"}
                                    </span>
                                  )}
                                </div>
                              )}
                              {(c.service_categories || []).includes("car") && c.car_cleaning_status === "pending" && (
                                <div className="mt-2 flex gap-1.5">
                                  <button
                                    onClick={() => setCarEligibility(c.user_id, "approved")}
                                    data-testid={`car-approve-${c.user_id}`}
                                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-amber-400/20 border border-amber-400/50 text-amber-200 hover:bg-amber-400/30"
                                  >Approve car</button>
                                  <button
                                    onClick={() => rejectCarWithReason(c.user_id)}
                                    data-testid={`car-reject-${c.user_id}`}
                                    className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-red-500/10 border border-red-400/40 text-red-300 hover:bg-red-500/20"
                                  >Reject car</button>
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-slate-300 text-xs">{c.city}{c.city && c.country ? ", " : ""}{c.country}</td>
                            <td className="p-3">
                              <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border ${V_STYLES[c.verification_status]}`}>
                                {c.verification_status.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="p-3" data-testid={`vendor-agreement-${c.user_id}`}>
                              {c.agreement_accepted_version ? (
                                <div className="text-[11px]">
                                  <div className={`font-mono ${c.agreement_ok ? "text-teal-300" : "text-amber-300"}`}>{c.agreement_accepted_version}</div>
                                  <div className="text-slate-500 text-[10px]">{c.agreement_accepted_at ? new Date(c.agreement_accepted_at).toLocaleString() : "—"}</div>
                                  {!c.agreement_ok && (
                                    <div className="mt-1 inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-400/40">
                                      needs re-accept · current {c.agreement_current_version}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-400/30 bg-slate-500/10 px-1.5 py-0.5 rounded">not accepted</span>
                              )}
                            </td>
                            <td className="p-3 text-right font-mono text-teal-300">{c.avg_rating || 0}</td>
                            <td className="p-3 text-right font-mono text-slate-300">{c.jobs_completed || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* CLIENTS & BOOKINGS */}
          <TabsContent value="clients" className="mt-6">
            <Card className="bg-white/[0.03] border-white/10 mb-6">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Segment</th>
                      <th className="text-right p-3">Bookings</th>
                      <th className="text-right p-3">Total spent</th>
                      <th className="text-left p-3">Last booking</th>
                      <th className="text-left p-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((c) => (
                      <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => openUser(c.id)}>
                        <td className="p-3">
                          <div className="text-sm text-slate-100 hover:text-teal-300">{c.full_name}</div>
                          <div className="text-[11px] text-slate-500">{c.email}</div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border ${c.has_business_job ? "bg-teal-500/10 text-teal-200 border-teal-400/40" : "bg-slate-500/10 text-slate-300 border-slate-400/30"}`}>
                            {c.has_business_job ? <Building2 className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                            {c.has_business_job ? "Business" : "Home"}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-slate-200">{c.bookings_count} <span className="text-slate-500 text-xs">/ {c.paid_count} paid</span></td>
                        <td className="p-3 text-right font-mono text-teal-300">{money(c.total_spent)}</td>
                        <td className="p-3 text-xs text-slate-400">{c.last_booking_at ? new Date(c.last_booking_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <tr><td colSpan={6} className="p-10 text-center text-slate-500">No clients match this search.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* All bookings with invoice actions */}
            <div className="text-xs font-mono uppercase tracking-[0.22em] text-slate-400 mb-3">All Bookings ({filteredJobs.length})</div>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="text-left p-3">Booking</th>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Scheduled</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map((j) => (
                      <tr key={j.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="p-3 font-mono text-[11px] text-slate-300">
                          <button onClick={() => openBooking(j.id)} data-testid={`booking-open-${j.id}`} className="text-teal-300 hover:underline">{j.id.slice(0, 8)}</button>
                        </td>
                        <td className="p-3">
                          <button onClick={() => j.client?.id && openUser(j.client.id)} data-testid={`booking-open-client-${j.id}`} className="text-left hover:text-teal-300" disabled={!j.client?.id}>
                            <div className="text-sm text-slate-100">{j.client?.full_name}</div>
                            <div className="text-[11px] text-slate-500">{j.client?.email}</div>
                          </button>
                        </td>
                        <td className="p-3 text-xs text-slate-300">
                          {j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : "—"}
                        </td>
                        <td className="p-3 text-xs text-slate-300">{(j.booking_type || "home").toUpperCase()}</td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.14em] border ${S_STYLES[j.status] || S_STYLES.pending}`}>{j.status}</span>
                        </td>
                        <td className="p-3 text-right font-mono text-slate-100">{money(j.total)}</td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => openReschedule(j)}
                              data-testid={`c-resch-${j.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-white/10 text-slate-200 hover:border-teal-400 hover:text-teal-300"
                            >
                              <Calendar className="w-3.5 h-3.5" /> Reschedule
                            </button>
                            {j.booking_type === "business" && (
                              <button
                                onClick={() => downloadInvoice(j)}
                                data-testid={`c-invoice-${j.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-teal-400/40 text-teal-200 hover:bg-teal-500/10"
                              >
                                <FileText className="w-3.5 h-3.5" /> PDF
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ESCROW LEDGER */}
          <TabsContent value="ledger" className="mt-6">
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-6">
                <div className="mb-4 text-sm text-slate-300">
                  Automated escrow distribution: <span className="text-teal-300 font-mono">20%</span> platform · <span className="text-blue-300 font-mono">80%</span> contractor on every paid job.
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                      <tr>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Booking</th>
                        <th className="text-left p-3">Client</th>
                        <th className="text-right p-3">Gross</th>
                        <th className="text-right p-3">Platform 20%</th>
                        <th className="text-right p-3">Cleaner 80%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidJobs.map((j) => (
                        <tr key={j.id} className="border-t border-white/5">
                          <td className="p-3 text-xs text-slate-400">{new Date(j.created_at).toLocaleDateString()}</td>
                          <td className="p-3 font-mono text-[11px] text-slate-300">{j.id.slice(0, 8)}</td>
                          <td className="p-3 text-slate-200 text-sm">{j.client?.full_name}</td>
                          <td className="p-3 text-right font-mono text-slate-100">{money(j.total)}</td>
                          <td className="p-3 text-right font-mono text-teal-300">{money(j.platform_fee)}</td>
                          <td className="p-3 text-right font-mono text-blue-300">{money(j.cleaner_payout)}</td>
                        </tr>
                      ))}
                      {paidJobs.length === 0 && (
                        <tr><td colSpan={6} className="p-10 text-center text-slate-500">No paid jobs yet.</td></tr>
                      )}
                    </tbody>
                    {paidJobs.length > 0 && (
                      <tfoot className="border-t-2 border-white/15">
                        <tr>
                          <td className="p-3 font-mono text-xs text-slate-400 uppercase tracking-[0.18em]" colSpan={3}>Totals</td>
                          <td className="p-3 text-right font-mono text-slate-100">{money(paidGmv)}</td>
                          <td className="p-3 text-right font-mono text-teal-300">{money(stats.platform_revenue)}</td>
                          <td className="p-3 text-right font-mono text-blue-300">{money(stats.cleaner_payouts)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DISPUTES / COMPLAINTS CONSOLE */}
          <TabsContent value="disputes" className="mt-6">
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              {["", "under_review", "no_issue_found", "customer_resolution", "cleaner_resolution", "other"].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setComplaintFilter(s)}
                  data-testid={`disputes-filter-${s || "all"}`}
                  className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] rounded border transition-colors ${
                    complaintFilter === s
                      ? "bg-teal-500/20 text-teal-200 border-teal-400/50"
                      : "bg-white/[0.03] text-slate-300 border-white/10 hover:text-teal-200"
                  }`}
                >
                  {s ? s.replace(/_/g, " ") : "all"}
                </button>
              ))}
              <div className="relative ml-auto max-w-xs w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={complaintQ}
                  onChange={(e) => setComplaintQ(e.target.value)}
                  placeholder="Search booking / customer / cleaner…"
                  data-testid="disputes-search"
                  className="pl-9 bg-white/[0.04] border-white/10 text-slate-100 placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-4">
              <b>Reminder —</b> Recording a determination here does <b>not</b> release, refund, or adjust any Stripe payment or 80/20 payout. Any monetary action must be executed through the existing Escrow / Payouts flows.
            </div>

            <Card className="bg-slate-900/40 border-white/5" data-testid="disputes-list-card">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.04] text-slate-400 font-mono text-[10px] uppercase tracking-[0.2em]">
                      <tr>
                        <th className="text-left p-3">Submitted</th>
                        <th className="text-left p-3">Booking</th>
                        <th className="text-left p-3">Customer</th>
                        <th className="text-left p-3">Cleaner</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-left p-3">Booking</th>
                        <th className="text-left p-3">Payment</th>
                        <th className="text-left p-3">Status</th>
                        <th className="p-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {complaints
                        .filter((c) => {
                          const s = complaintQ.trim().toLowerCase();
                          if (!s) return true;
                          return [c.job_id, c.customer?.email, c.customer?.full_name, c.cleaner?.email, c.cleaner?.full_name, c.description]
                            .filter(Boolean)
                            .some((v) => v.toString().toLowerCase().includes(s));
                        })
                        .map((c) => (
                          <tr key={c.id} data-testid={`dispute-row-${c.id}`} className="hover:bg-white/[0.02]">
                            <td className="p-3 font-mono text-[11px] text-slate-400">{c.submitted_at ? new Date(c.submitted_at).toLocaleString() : "—"}</td>
                            <td className="p-3 font-mono text-[11px] text-slate-200">{c.job_id?.slice(0, 8)}</td>
                            <td className="p-3 text-slate-200 text-xs">{c.customer?.full_name || c.customer?.email || "—"}</td>
                            <td className="p-3 text-slate-300 text-xs">{c.cleaner?.full_name || c.cleaner?.email || "—"}</td>
                            <td className="p-3 text-slate-200 text-xs">{COMPLAINT_ISSUE_LABEL[c.issue_type] || c.issue_type}</td>
                            <td className="p-3 text-slate-300 text-xs max-w-xs truncate" title={c.description}>{c.description}</td>
                            <td className="p-3">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${S_STYLES[c.booking?.status] || "bg-slate-500/15 text-slate-200 border-slate-400/30"}`}>
                                {c.booking?.status || "—"}
                              </span>
                            </td>
                            <td className="p-3 font-mono text-[11px] text-slate-300">{c.booking?.payment_status || "—"}</td>
                            <td className="p-3">
                              <Badge className={`text-[10px] ${COMPLAINT_STATUS_STYLES[c.status] || ""}`}>{(c.status || "under_review").replace(/_/g, " ")}</Badge>
                            </td>
                            <td className="p-3 text-right">
                              <Button
                                size="sm"
                                onClick={() => { setOpenComplaint(c); setDetStatus(c.status || "under_review"); setDetNote(""); }}
                                data-testid={`dispute-open-${c.id}`}
                                className="bg-teal-500/15 text-teal-200 border border-teal-400/40 hover:bg-teal-500/25 h-7 text-[11px]"
                              >
                                <ImageIcon className="w-3 h-3 mr-1" />Review
                              </Button>
                            </td>
                          </tr>
                        ))}
                      {complaints.length === 0 && (
                        <tr><td className="p-10 text-center text-slate-500" colSpan={10}>No complaints yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        )}
      </div>

      {/* Reschedule dialog */}
      <Dialog open={!!reschJob} onOpenChange={(o) => !o && setReschJob(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-slate-100" data-testid="resch-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule booking</DialogTitle>
            <DialogDescription className="text-slate-400">
              Admin override — the late fee is waived. Client is notified via email on save.
            </DialogDescription>
          </DialogHeader>
          {reschJob && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-400">
                Booking <span className="font-mono text-slate-200">{reschJob.id.slice(0, 8)}</span> · {reschJob.client?.full_name}
              </div>
              <div className="text-slate-400">
                Current: <span className="text-slate-200">{reschJob.scheduled_at ? new Date(reschJob.scheduled_at).toLocaleString() : "—"}</span>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">New date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={reschDate}
                  onChange={(e) => setReschDate(e.target.value)}
                  data-testid="resch-input"
                  className="mt-2 bg-white/[0.04] border-white/10 text-slate-100"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschJob(null)} className="bg-transparent border-white/10 text-slate-200">Cancel</Button>
            <Button onClick={submitReschedule} data-testid="resch-submit" className="bg-teal-500 text-slate-950 hover:bg-teal-400">Save schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents & Verification dialog (Step 12 fix) — replaces the
          single "View ID document" link. Sensitive URLs live only in the
          response body and are opened via <a target="_blank"> without ever
          appearing in a query string. */}
      <Dialog open={!!docsCleaner} onOpenChange={(o) => { if (!o) { setDocsCleaner(null); setDocsData(null); } }}>
        <DialogContent
          className="bg-slate-900 border-white/10 text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto"
          data-testid="cleaner-docs-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-300" />
              Documents &amp; Verification
              {docsCleaner?.cleanbaz_id && (
                <span className="ml-2 font-mono text-[10px] tracking-widest px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-200 border border-teal-400/30">
                  {docsCleaner.cleanbaz_id}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Admin-only. Never share these URLs in tickets, chats, or logs. Access is audit-logged.
            </DialogDescription>
          </DialogHeader>
          {docsLoading && <div className="text-slate-400 text-sm py-6">Loading…</div>}
          {docsData && (
            <div className="space-y-3">
              <div className="text-xs text-slate-300">
                <span className="text-slate-400">Cleaner:</span>{" "}
                <b>{docsCleaner?.legal_first_name || docsCleaner?.user?.full_name} {docsCleaner?.legal_last_name}</b>
                {" · "}
                <span className="text-slate-500 font-mono">{docsCleaner?.user?.email}</span>
              </div>
              <div className="rounded border border-white/5 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.04] text-slate-400 font-mono text-[10px] uppercase tracking-[0.2em]">
                    <tr>
                      <th className="text-left p-3">Document</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Submitted</th>
                      <th className="text-left p-3">Expires</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {docsData.documents.map((d) => {
                      const styles = {
                        approved:      "bg-teal-500/15 text-teal-200 border-teal-400/40",
                        submitted:     "bg-amber-500/15 text-amber-200 border-amber-400/40",
                        rejected:      "bg-red-500/15 text-red-300 border-red-400/40",
                        expired:       "bg-red-500/15 text-red-300 border-red-400/40",
                        not_submitted: "bg-slate-500/15 text-slate-300 border-slate-400/30",
                      }[d.status] || "bg-slate-500/15 text-slate-300 border-slate-400/30";
                      return (
                        <tr key={d.id} data-testid={`doc-row-${d.kind}`}>
                          <td className="p-3 align-top">
                            <div className="text-slate-100 text-sm">{d.label}</div>
                            <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">{d.kind}</div>
                            {d.meta?.carrier && <div className="text-[11px] text-slate-400 mt-1">Carrier: {d.meta.carrier} · Policy last-4 <span className="font-mono">{d.meta.policy_number_last4 || "—"}</span></div>}
                            {d.meta?.year_make_model && <div className="text-[11px] text-slate-400 mt-1">{d.meta.vehicle_type?.toUpperCase()} · {d.meta.year_make_model} · Plate {d.meta.plate}</div>}
                            {d.reason && <div className="text-[11px] text-red-300 mt-1">Reason: {d.reason}</div>}
                          </td>
                          <td className="p-3 align-top">
                            <Badge className={`text-[10px] ${styles}`} data-testid={`doc-status-${d.kind}`}>
                              {d.status.replace(/_/g, " ")}
                            </Badge>
                            {d.verified_at && <div className="text-[10px] text-slate-500 mt-1">by {d.verified_by || "—"}</div>}
                          </td>
                          <td className="p-3 align-top text-[11px] text-slate-300 font-mono">
                            {d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="p-3 align-top text-[11px] font-mono">
                            {d.expiration_date ? (
                              <span className={d.status === "expired" ? "text-red-300" : "text-slate-300"}>{d.expiration_date}</span>
                            ) : "—"}
                          </td>
                          <td className="p-3 align-top text-right">
                            <div className="flex gap-1.5 justify-end flex-wrap">
                              {d.url && (
                                <a
                                  href={d.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`doc-view-${d.kind}`}
                                  className="inline-flex items-center gap-1 text-[11px] text-teal-300 hover:text-teal-200 underline"
                                >
                                  <ExternalLink className="w-3 h-3" /> View
                                </a>
                              )}
                              {d.review_action && d.url && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => verifyDoc(d, d.review_action.statuses[0])}
                                    data-testid={`doc-approve-${d.kind}`}
                                    className="h-6 px-2 text-[10px] bg-teal-500 hover:bg-teal-400 text-slate-950"
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const reason = window.prompt("Reject reason (visible to the cleaner):", "Photo unclear — please re-upload.");
                                      if (reason == null) return;
                                      verifyDoc(d, "rejected", reason.trim() || null);
                                    }}
                                    data-testid={`doc-reject-${d.kind}`}
                                    className="h-6 px-2 text-[10px] bg-transparent border-red-400/40 text-red-300 hover:bg-red-500/10"
                                  >
                                    <XCircle className="w-3 h-3 mr-1" />Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-400/30 rounded p-3" data-testid="cleaner-docs-notice">
                {docsData.notice}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDocsCleaner(null); setDocsData(null); }} className="bg-transparent border-white/10 text-slate-200">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute / Complaint review dialog */}
      <Dialog open={!!openComplaint} onOpenChange={(o) => !o && setOpenComplaint(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquareWarning className="w-5 h-5 text-amber-300" /> Complaint review</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              CLEANBAZ reviews the evidence and applicable policy before recording a determination. This action does not release, refund, or adjust any Stripe payment or 80/20 payout.
            </DialogDescription>
          </DialogHeader>
          {openComplaint && (
            <div className="space-y-4 text-sm">
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-slate-950/40 border-white/5">
                  <CardContent className="p-4 text-xs space-y-2" data-testid="dispute-detail">
                    <div><span className="text-slate-400">Booking:</span> <span className="font-mono text-slate-200">{openComplaint.job_id}</span></div>
                    <div><span className="text-slate-400">Submitted:</span> <span className="text-slate-200">{openComplaint.submitted_at ? new Date(openComplaint.submitted_at).toLocaleString() : "—"}</span></div>
                    <div><span className="text-slate-400">Customer:</span> <span className="text-slate-200">{openComplaint.customer?.full_name || "—"} · {openComplaint.customer?.email || "—"}</span></div>
                    <div><span className="text-slate-400">Cleaner:</span> <span className="text-slate-200">{openComplaint.cleaner?.full_name || "—"} · {openComplaint.cleaner?.email || "—"}</span></div>
                    <div><span className="text-slate-400">Issue type:</span> <b className="text-slate-100">{COMPLAINT_ISSUE_LABEL[openComplaint.issue_type] || openComplaint.issue_type}</b></div>
                    <div><span className="text-slate-400">Booking status:</span> {openComplaint.booking?.status || openComplaint.booking_status}</div>
                    <div><span className="text-slate-400">Payment status:</span> {openComplaint.booking?.payment_status || openComplaint.payment_status}</div>
                    {openComplaint.determination_at && (
                      <div><span className="text-slate-400">Last determination:</span> <b className="text-slate-100">{openComplaint.status?.replace(/_/g," ")}</b> · {new Date(openComplaint.determination_at).toLocaleString()} · {openComplaint.determination_by}</div>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-slate-950/40 border-white/5">
                  <CardContent className="p-4 text-xs" data-testid="dispute-description">
                    <div className="text-slate-400 mb-2">Customer description</div>
                    <div className="whitespace-pre-wrap text-slate-100 max-h-40 overflow-y-auto">{openComplaint.description}</div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" />Before / After evidence</div>
                <Tabs defaultValue="before">
                  <TabsList>
                    <TabsTrigger value="before" data-testid="dispute-photos-before">Before</TabsTrigger>
                    <TabsTrigger value="after"  data-testid="dispute-photos-after">After</TabsTrigger>
                  </TabsList>
                  <TabsContent value="before" className="mt-3">
                    <JobPhotoDocumentation jobId={openComplaint.job_id} phase="before" mode="view" />
                  </TabsContent>
                  <TabsContent value="after" className="mt-3">
                    <JobPhotoDocumentation jobId={openComplaint.job_id} phase="after" mode="view" />
                  </TabsContent>
                </Tabs>
              </div>

              <Card className="bg-slate-950/40 border-white/5">
                <CardContent className="p-4 space-y-3 text-xs" data-testid="dispute-determination-form">
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2"><ScrollText className="w-3.5 h-3.5" />Record CLEANBAZ determination</div>
                  <div>
                    <Label className="text-slate-300">Determination</Label>
                    <select
                      value={detStatus}
                      onChange={(e) => setDetStatus(e.target.value)}
                      data-testid="dispute-det-status"
                      className="mt-1 w-full h-10 border rounded-md px-3 text-sm bg-slate-900 border-white/10 text-slate-100"
                    >
                      <option value="under_review">Under Review</option>
                      <option value="no_issue_found">No Issue Found</option>
                      <option value="customer_resolution">Customer Resolution</option>
                      <option value="cleaner_resolution">Cleaner Resolution</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Internal admin note (audit-logged, not sent to customer)</Label>
                    <textarea
                      rows={3}
                      value={detNote}
                      onChange={(e) => setDetNote(e.target.value)}
                      data-testid="dispute-det-note"
                      placeholder="Evidence reviewed, insurer contacted, follow-up steps…"
                      className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-slate-900 border-white/10 text-slate-100"
                    />
                  </div>
                  {openComplaint.admin_notes?.length > 0 && (
                    <div>
                      <div className="text-slate-400 mb-1">Prior admin notes</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {openComplaint.admin_notes.map((n, i) => (
                          <div key={i} className="p-2 rounded border border-white/5 bg-slate-950/60 text-[11px]">
                            <div className="text-slate-500">{new Date(n.at).toLocaleString()} · {n.actor_email}</div>
                            <div className="text-slate-200 whitespace-pre-wrap">{n.note}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenComplaint(null)} className="bg-transparent border-white/10 text-slate-200">Close</Button>
            <Button
              data-testid="dispute-det-submit"
              onClick={async () => {
                try {
                  await api.post(`/admin/complaints/${openComplaint.id}/determination`, {
                    status: detStatus,
                    admin_note: detNote,
                  });
                  toast.success("Determination recorded");
                  setOpenComplaint(null);
                  loadComplaints();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to record");
                }
              }}
              className="bg-teal-500 text-slate-950 hover:bg-teal-400"
            >
              Save determination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------------
          Insurance Verify Dialog — reuses POST /admin/insurance/verify.
          Backend audit-logs actor, timestamp, status, reason and emails
          the cleaner. Cleaner eligibility (transport-required jobs) is
          re-evaluated immediately by _check_accept_eligibility.
          --------------------------------------------------------------- */}
      <Dialog open={!!insVerifyRow} onOpenChange={(o) => !o && setInsVerifyRow(null)}>
        <DialogContent data-testid="verify-ins-dialog">
          <DialogHeader>
            <DialogTitle>Review insurance submission</DialogTitle>
            <DialogDescription>
              Documents are visible to authorized admins only. Full policy numbers are never returned in this view.
            </DialogDescription>
          </DialogHeader>
          {insVerifyRow && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border border-white/10 bg-slate-950/40 p-3 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-teal-300">{insVerifyRow.cleanbaz_id || "—"}</span>
                  <span className="text-slate-100 font-semibold">{insVerifyRow.cleaner?.email}</span>
                </div>
                <div className="text-xs text-slate-400">
                  <span className="uppercase tracking-widest text-[10px] text-slate-500">{insVerifyRow.insurance_type}</span>
                  {" · "}<b className="text-slate-200">{insVerifyRow.carrier || "—"}</b>
                  {" · Policy "}<span className="font-mono">••••{insVerifyRow.policy_number_last4 || "----"}</span>
                </div>
                <div className="text-xs text-slate-400">
                  Effective <span className="font-mono">{insVerifyRow.effective_date || "—"}</span>
                  {" · Exp "}<span className="font-mono">{insVerifyRow.expiration_date || "—"}</span>
                </div>
                {insVerifyRow.document_url && (
                  <button type="button"
                     onClick={async () => {
                       try { await openProtectedFile(insVerifyRow.document_url); }
                       catch (err) { toast.error(err?.response?.status === 403 ? "Not authorized to view this document." : "Unable to load proof document."); }
                     }}
                     className="inline-flex items-center gap-1 text-teal-300 hover:underline text-xs mt-1 bg-transparent p-0"
                     data-testid="verify-ins-dialog-doc">
                    <Eye className="w-3.5 h-3.5" /> View proof (admin-only)
                  </button>
                )}
              </div>
              <div>
                <Label>Decision</Label>
                <select value={insVerifyStatus} onChange={(e) => setInsVerifyStatus(e.target.value)}
                  data-testid="verify-ins-status"
                  className="mt-1 w-full h-10 border rounded-md px-3 text-sm bg-slate-950 border-white/10 text-slate-100">
                  <option value="approved">Approve</option>
                  <option value="rejected">Reject</option>
                  <option value="needs_resubmission">Needs resubmission</option>
                </select>
              </div>
              <div>
                <Label>Reason / notes {insVerifyStatus !== "approved" ? "*" : "(optional)"}</Label>
                <textarea value={insVerifyReason} onChange={(e) => setInsVerifyReason(e.target.value)} rows={3}
                  data-testid="verify-ins-reason"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-slate-950 border-white/10 text-slate-100" />
              </div>
              <div>
                <Label>Expiration date override (optional)</Label>
                <Input type="date" value={insVerifyExpiry} onChange={(e) => setInsVerifyExpiry(e.target.value)}
                  data-testid="verify-ins-expiry"
                  className="mt-1 bg-slate-950 border-white/10 text-slate-100" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsVerifyRow(null)}
              className="bg-transparent border-white/10 text-slate-200">Cancel</Button>
            <Button onClick={submitInsuranceVerify} data-testid="verify-ins-submit"
              className="bg-teal-500 text-slate-950 hover:bg-teal-400">
              Save decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
